import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Key, Plus, Trash2, Copy, AlertTriangle, Eye, EyeOff, CheckCircle, RefreshCw, Download } from 'lucide-react';

function PartnerApiKeys({ currentUser }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null); // résultat de la création (clé en clair, affichée 1 seule fois)
  const [form, setForm] = useState({ name: '', webhook_url: '' });
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [deliveries, setDeliveries] = useState(null); // { keyId, list }

  const isAdmin = currentUser && currentUser.role === 'admin';

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get('/api/partner-keys', { headers: { Authorization: `Bearer ${token}` } });
      setKeys(r.data);
    } catch (e) {
      console.error('fetchKeys', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchKeys(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { alert('Le nom est requis'); return; }
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post('/api/partner-keys', form, { headers: { Authorization: `Bearer ${token}` } });
      setNewKeyData(r.data);
      setShowCreate(false);
      setForm({ name: '', webhook_url: '' });
      await fetchKeys();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (key) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/partner-keys/${key.id}`, { active: !key.active }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchKeys();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Révoquer définitivement la clé "${key.name}" ? Le partenaire ne pourra plus l'utiliser.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/partner-keys/${key.id}`, { headers: { Authorization: `Bearer ${token}` } });
      await fetchKeys();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const [downloadingId, setDownloadingId] = useState(null);
  const handleDownloadDoc = async (key) => {
    setDownloadingId(key.id);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`/api/partner-keys/${key.id}/documentation`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const safeName = (key.name || 'partenaire').replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'partenaire';
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/markdown;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `doc-integration-${safeName}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur téléchargement doc : ' + (err.response?.data?.error || err.message));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewDeliveries = async (key) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`/api/partner-keys/${key.id}/webhook-deliveries`, { headers: { Authorization: `Bearer ${token}` } });
      setDeliveries({ key, list: r.data });
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const copy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <main className="dashboard-main" style={{ background: 'transparent' }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '30px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <h2 style={{ margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Key size={24} /> Clés API Partenaires
            </h2>
            <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '14px' }}>
              Crée une clé API pour qu'un CRM externe accède à tes clients et envoie des liens Monetico en ton nom.
            </p>
          </div>
          <button
            onClick={() => { setShowCreate(true); setNewKeyData(null); }}
            style={{
              background: 'linear-gradient(135deg, #00f2fe, #4facfe)', color: 'white', border: 'none',
              padding: '12px 22px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 15px rgba(79,172,254,0.4)'
            }}>
            <Plus size={16} /> Nouvelle clé
          </button>
        </div>

        {/* Bandeau clé fraîchement créée */}
        {newKeyData && (
          <div style={{ background: 'linear-gradient(135deg,#ecfdf5,#d1fae5)', border: '2px solid #10b981', borderRadius: '12px', padding: '18px', marginBottom: '25px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
              <AlertTriangle size={22} color="#059669" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong style={{ color: '#065f46', fontSize: '15px' }}>Clé créée — copie-la maintenant !</strong>
                <p style={{ margin: '4px 0 0', color: '#047857', fontSize: '13px' }}>
                  Cette clé ne sera plus jamais affichée. Si tu la perds, il faudra en créer une nouvelle.
                </p>
              </div>
            </div>

            <label style={{ fontSize: '11px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Clé API (X-API-Key)</label>
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', marginBottom: '12px' }}>
              <input type="text" value={newKeyData.key} readOnly onClick={(e) => e.target.select()}
                style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #10b981', borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px', background: 'white' }} />
              <button onClick={() => copy(newKeyData.key, 'key')}
                style={{ background: copiedField === 'key' ? '#10b981' : '#065f46', color: 'white', border: 'none', padding: '0 16px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {copiedField === 'key' ? <><CheckCircle size={14} /> Copié</> : <><Copy size={14} /> Copier</>}
              </button>
            </div>

            <label style={{ fontSize: '11px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Secret webhook (pour vérifier les signatures HMAC)</label>
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
              <input type={showWebhookSecret ? 'text' : 'password'} value={newKeyData.webhook_secret} readOnly
                style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #10b981', borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px', background: 'white' }} />
              <button onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                style={{ background: '#f8fafc', border: '1.5px solid #10b981', padding: '0 12px', borderRadius: '8px', cursor: 'pointer' }}>
                {showWebhookSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={() => copy(newKeyData.webhook_secret, 'secret')}
                style={{ background: copiedField === 'secret' ? '#10b981' : '#065f46', color: 'white', border: 'none', padding: '0 16px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {copiedField === 'secret' ? <CheckCircle size={14} /> : <Copy size={14} />}
              </button>
            </div>

            <button onClick={() => setNewKeyData(null)} style={{ marginTop: '14px', background: 'transparent', border: '1px solid #065f46', color: '#065f46', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
              J'ai copié, fermer
            </button>
          </div>
        )}

        {/* Modal création */}
        {showCreate && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: 'white', borderRadius: '16px', padding: '30px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <h3 style={{ margin: 0, color: '#0f172a' }}>Nouvelle clé API partenaire</h3>
              <form onSubmit={handleCreate} style={{ marginTop: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>Nom de la clé *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="ex: CRM Partenaire ABC" required autoFocus
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>URL webhook (optionnel)</label>
                  <input type="url" value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                    placeholder="https://crm-partenaire.com/webhook/payments"
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748b' }}>
                    Si renseigné, les paiements Monetico seront poussés ici en temps réel (POST signé HMAC-SHA256).
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button type="button" onClick={() => setShowCreate(false)}
                    style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
                    Annuler
                  </button>
                  <button type="submit" disabled={creating}
                    style={{ background: 'linear-gradient(135deg,#00f2fe,#4facfe)', color: 'white', border: 'none', padding: '10px 22px', borderRadius: '8px', cursor: creating ? 'wait' : 'pointer', fontWeight: 700, opacity: creating ? 0.6 : 1 }}>
                    {creating ? 'Création…' : 'Créer la clé'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Liste */}
        {loading ? (
          <p style={{ textAlign: 'center', color: '#64748b' }}>Chargement…</p>
        ) : keys.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '12px' }}>
            <Key size={32} color="#94a3b8" style={{ margin: '0 auto 12px' }} />
            <p style={{ color: '#64748b', margin: 0 }}>Aucune clé API pour l'instant.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: '12px', textTransform: 'uppercase' }}>Nom</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: '12px', textTransform: 'uppercase' }}>Préfixe</th>
                  {isAdmin && <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: '12px', textTransform: 'uppercase' }}>Scope</th>}
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: '12px', textTransform: 'uppercase' }}>Webhook</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: '12px', textTransform: 'uppercase' }}>Dern. utilisation</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: '12px', textTransform: 'uppercase' }}>Statut</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: '12px', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 12px' }}>
                      <strong style={{ color: '#0f172a' }}>{k.name}</strong>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        Créée le {new Date(k.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </td>
                    <td style={{ padding: '14px 12px', fontFamily: 'monospace', color: '#475569', fontSize: '12px' }}>{k.key_prefix}…</td>
                    {isAdmin && (
                      <td style={{ padding: '14px 12px' }}>
                        {k.user_nom}
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{k.user_role}</div>
                      </td>
                    )}
                    <td style={{ padding: '14px 12px', fontSize: '12px', color: '#475569', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {k.webhook_url || <span style={{ color: '#cbd5e1' }}>—</span>}
                      {k.webhook_calls > 0 && (
                        <button onClick={() => handleViewDeliveries(k)} style={{ marginLeft: '8px', background: 'transparent', border: 'none', color: '#4facfe', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline' }}>
                          ({k.webhook_calls} livraisons)
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '14px 12px', fontSize: '12px', color: '#475569' }}>
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleString('fr-FR') : <span style={{ color: '#cbd5e1' }}>Jamais</span>}
                    </td>
                    <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                      <button onClick={() => handleToggleActive(k)}
                        style={{
                          background: k.active ? '#dcfce7' : '#fee2e2',
                          color: k.active ? '#15803d' : '#b91c1c',
                          border: 'none', padding: '4px 12px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase'
                        }}>
                        {k.active ? 'Active' : 'Désactivée'}
                      </button>
                    </td>
                    <td style={{ padding: '14px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleDownloadDoc(k)} disabled={downloadingId === k.id}
                        title="Télécharger la doc d'intégration (personnalisée avec le secret de cette clé)"
                        style={{ background: 'transparent', border: 'none', color: '#2563eb', cursor: downloadingId === k.id ? 'wait' : 'pointer', padding: '6px' }}>
                        <Download size={16} />
                      </button>
                      <button onClick={() => handleDelete(k)} title="Révoquer définitivement"
                        style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '6px' }}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal livraisons webhook */}
        {deliveries && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '800px', width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>Livraisons webhook — {deliveries.key.name}</h3>
                <button onClick={() => setDeliveries(null)} style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
              </div>
              {deliveries.list.length === 0 ? (
                <p style={{ color: '#64748b' }}>Aucune livraison.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Événement</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>HTTP</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Durée</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.list.map(d => (
                      <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px' }}>{new Date(d.delivered_at).toLocaleString('fr-FR')}</td>
                        <td style={{ padding: '8px' }}><code>{d.event}</code></td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <span style={{ background: d.response_status >= 200 && d.response_status < 300 ? '#dcfce7' : '#fee2e2', color: d.response_status >= 200 && d.response_status < 300 ? '#15803d' : '#b91c1c', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                            {d.response_status || 'ERR'}
                          </span>
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', color: '#64748b' }}>{d.duration_ms}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ──────────── DOCUMENTATION COMPLÈTE PARTENAIRE ──────────── */}
        <PartnerDocumentation />
      </div>
    </main>
  );
}

// Styles helpers pour les blocs de code
const codeBlock = {
  background: '#0f172a', color: '#e2e8f0', padding: '14px 16px',
  borderRadius: '8px', fontFamily: 'Menlo, Monaco, monospace',
  fontSize: '12px', lineHeight: 1.55, overflowX: 'auto',
  whiteSpace: 'pre', margin: '8px 0 16px 0', border: '1px solid #1e293b'
};
const inlineCode = {
  background: '#e2e8f0', padding: '2px 7px', borderRadius: '4px',
  fontFamily: 'Menlo, Monaco, monospace', fontSize: '12px', color: '#0f172a'
};
const sectionTitle = {
  marginTop: '26px', marginBottom: '8px', color: '#0f172a',
  fontSize: '16px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px'
};
const subTitle = {
  marginTop: '16px', marginBottom: '6px', color: '#1e293b',
  fontSize: '14px', fontWeight: 700
};
const endpointBox = {
  border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '16px',
  marginBottom: '14px', background: 'white'
};
const httpVerb = (m) => ({
  display: 'inline-block', padding: '3px 9px', borderRadius: '4px',
  fontFamily: 'Menlo, Monaco, monospace', fontSize: '11px', fontWeight: 700,
  marginRight: '8px', color: 'white',
  background: m === 'GET' ? '#10b981' : m === 'POST' ? '#3b82f6' : m === 'PUT' ? '#f59e0b' : '#ef4444'
});

function PartnerDocumentation() {
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const [copied, setCopied] = useState(false);

  const fullDocText = `# API Partenaire - Documentation d'intégration

## Authentification
Toutes les requêtes doivent inclure le header :
  X-API-Key: csk_xxxxxxxxxxxxxxxxxxxxxxx

Base URL : ${baseUrl}/api/v1/partner

## Endpoints clients
GET  /clients?search=&limit=50&offset=0   → liste des clients
GET  /clients/:id                          → détail
POST /clients                              → créer (nom, prenom, email, telephone requis)

## Endpoints paiement Monetico
POST /clients/:id/payment-links            → générer + envoyer un lien
  Body : { "type": "immediat"|"mensuel", "amount": 89, "send_email": true, "send_sms": true }
  Réponse : { payment_link: {url, token, ...}, sent: { email: {...}, sms: {...} } }

GET  /payments?statut=PAYE&since=2026-01-01  → liste paiements
GET  /payments/:reference                    → détail paiement

## Opérations Monetico (limitées à TES paiements)
GET  /payments/:reference/state              → état réel Monetico (AU/PA/RE...)
POST /payments/:reference/capture            → recouvrer (encaisser)
  Body (optionnel) : { "montant_a_capturer": 50 }   // partiel ; défaut = montant total
POST /payments/:reference/stop-recurrence    → résilier l'abonnement (stoppe les prélèvements)
POST /payments/:reference/refund             → rembourser
  Body (optionnel) : { "montant": 30 }              // remboursement partiel ; défaut = total

## Webhook (si URL configurée à la création de la clé)
POST <ton-url>
Headers :
  X-Webhook-Event: payment.paid | payment.refused | payment.updated
  X-Webhook-Signature: <HMAC-SHA256 du body brut, signé avec ton secret>
Body JSON :
  {
    "event": "payment.paid",
    "delivered_at": "ISO-8601",
    "payment": { "reference", "statut", "montant", "monetico_date", ... },
    "client":  { "id", "numero_client", "nom", "prenom", "email", ... }
  }

Vérification de signature (Node.js) :
  const crypto = require('crypto');
  const sig = req.headers['x-webhook-signature'];
  const expected = crypto.createHmac('sha256', SECRET).update(req.rawBody).digest('hex');
  if (sig !== expected) return res.status(401).send('Bad signature');
`;

  const copyDoc = () => {
    navigator.clipboard.writeText(fullDocText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const downloadDoc = () => {
    const url = window.URL.createObjectURL(new Blob([fullDocText], { type: 'text/markdown;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'doc-api-partenaire.md';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{ marginTop: '36px', background: 'linear-gradient(180deg,#f8fafc,#ffffff)', borderRadius: '14px', padding: '24px', border: '1.5px solid #e2e8f0' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '18px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#0f172a', fontSize: '22px' }}>📘 Documentation d'intégration</h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>
            À transmettre à ton partenaire pour qu'il intègre l'API à son CRM.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={downloadDoc}
            style={{
              background: '#2563eb', color: 'white', border: 'none',
              padding: '10px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px'
            }}>
            <Download size={15} /> Télécharger (.md)
          </button>
          <button onClick={copyDoc}
            style={{
              background: copied ? '#10b981' : '#0f172a', color: 'white', border: 'none',
              padding: '10px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px'
            }}>
            {copied ? <><CheckCircle size={15} /> Doc copiée</> : <><Copy size={15} /> Copier la doc</>}
          </button>
        </div>
      </div>

      {/* ═══════ 1. PRINCIPE ═══════ */}
      <h3 style={sectionTitle}>🎯 À quoi sert cette clé API ?</h3>
      <p style={{ color: '#475569', lineHeight: 1.7, margin: 0 }}>
        Cette clé permet à ton CRM d'envoyer des liens de paiement Monetico à <strong>tes clients</strong> sans
        passer par notre interface. Ton CRM agit "au nom" de ton commercial dans notre système :
      </p>
      <ul style={{ color: '#475569', lineHeight: 1.8, marginTop: '8px' }}>
        <li>📋 <strong>Consulter et créer des fiches client</strong> dans notre CRM</li>
        <li>💳 <strong>Générer un lien de paiement Monetico</strong> (carte bancaire, paiement unique ou abonnement mensuel)</li>
        <li>✉️ <strong>Envoyer ce lien par email</strong> (template HTML inclus avec ton branding, anti-spam via lien raccourci)</li>
        <li>📱 <strong>Envoyer ce lien par SMS</strong> (format optimisé pour l'activation)</li>
        <li>📊 <strong>Suivre les paiements en temps réel</strong> (statut, montant, date, mode de paiement)</li>
        <li>💶 <strong>Recouvrer (encaisser) un paiement</strong> — total ou partiel (geste commercial)</li>
        <li>⛔ <strong>Résilier une récurrence</strong> (stopper un abonnement)</li>
        <li>↩️ <strong>Rembourser</strong> un paiement (total ou partiel)</li>
        <li>🔍 <strong>Consulter l'état Monetico réel</strong> d'un paiement (autorisé / recouvré / refusé)</li>
        <li>🔔 <strong>Recevoir un webhook à chaque transaction</strong> pour synchroniser ton CRM</li>
      </ul>

      <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '12px 14px', marginTop: '14px' }}>
        <strong style={{ color: '#92400e' }}>🔒 Étendue limitée :</strong>
        <span style={{ color: '#78350f', fontSize: '13px', marginLeft: '6px' }}>
          la clé donne accès à <strong>tout le pouvoir Monetico (comme l'admin)</strong> mais
          <strong> uniquement sur les clients du commercial concerné et leurs paiements</strong>.
          Aucun accès aux devis, factures, commissions ou autres clients du CRM.
        </span>
      </div>

      {/* ═══════ 2. AUTHENTIFICATION ═══════ */}
      <h3 style={sectionTitle}>🔑 1. Authentification</h3>
      <p style={{ color: '#475569', margin: 0 }}>
        Ajouter le header <code style={inlineCode}>X-API-Key</code> à <strong>toutes</strong> les requêtes :
      </p>
      <pre style={codeBlock}>{`X-API-Key: csk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</pre>
      <p style={{ color: '#475569', margin: 0 }}><strong>Base URL :</strong></p>
      <pre style={codeBlock}>{`${baseUrl}/api/v1/partner`}</pre>

      {/* ═══════ 3. ENDPOINTS CLIENTS ═══════ */}
      <h3 style={sectionTitle}>👥 2. Gestion des clients</h3>

      <div style={endpointBox}>
        <div><span style={httpVerb('GET')}>GET</span><code style={inlineCode}>/clients</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>
          Liste paginée des clients du commercial. Paramètres optionnels :
          <code style={inlineCode}>search</code>, <code style={inlineCode}>limit</code> (max 200, défaut 50),
          <code style={inlineCode}>offset</code>.
        </p>
        <p style={subTitle}>Exemple curl :</p>
        <pre style={codeBlock}>{`curl "${baseUrl}/api/v1/partner/clients?search=dupont&limit=20" \\
  -H "X-API-Key: csk_xxx..."`}</pre>
        <p style={subTitle}>Réponse :</p>
        <pre style={codeBlock}>{`{
  "total": 42,
  "limit": 20,
  "offset": 0,
  "clients": [
    {
      "id": 208,
      "numero_client": "00000208",
      "nom": "Dupont",
      "prenom": "Jean",
      "email": "jean.dupont@example.com",
      "telephone": "06 12 34 56 78",
      "entreprise": "Dupont SARL",
      "adresse": "12 rue de Paris, 75001 Paris",
      "numero_siret": "12345678901234",
      "paye": false,
      "client_signe": false,
      "montant_paye": null,
      "created_at": "2026-05-15T14:30:00.000Z"
    }
  ]
}`}</pre>
      </div>

      <div style={endpointBox}>
        <div><span style={httpVerb('GET')}>GET</span><code style={inlineCode}>/clients/:id</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>Détail d'un client unique.</p>
      </div>

      <div style={endpointBox}>
        <div><span style={httpVerb('POST')}>POST</span><code style={inlineCode}>/clients</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>
          Créer un nouveau client. <strong>Auto-assigné</strong> au commercial propriétaire de la clé.
        </p>
        <p style={subTitle}>Body requis :</p>
        <pre style={codeBlock}>{`{
  "nom": "Dupont",            // requis
  "prenom": "Jean",           // requis
  "email": "j@example.com",   // requis
  "telephone": "0612345678",  // requis
  "entreprise": "ACME SARL",  // optionnel
  "adresse": "12 rue...",     // optionnel
  "numero_siret": "12345..."  // optionnel
}`}</pre>
        <p style={subTitle}>Réponse 201 : objet client (mêmes champs que GET).</p>
        <p style={subTitle}>Erreur 409 : email ou téléphone déjà existant.</p>
      </div>

      {/* ═══════ 4. PAYMENT LINKS ═══════ */}
      <h3 style={sectionTitle}>💳 3. Générer et envoyer un lien Monetico</h3>

      <div style={endpointBox}>
        <div><span style={httpVerb('POST')}>POST</span><code style={inlineCode}>/clients/:id/payment-links</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>
          Endpoint le plus important : génère un lien de paiement Monetico et (optionnellement) l'envoie
          par email/SMS via Brevo en un seul appel. L'URL est automatiquement raccourcie (urlr.me)
          avant envoi pour passer les filtres anti-spam.
        </p>
        <p style={subTitle}>Body :</p>
        <pre style={codeBlock}>{`{
  "type": "immediat",      // "immediat" | "mensuel"
  "amount": 89.00,         // EUR, minimum 1
  "dossier": "DOSS-2026-042",       // optionnel - réf interne
  "description": "Abonnement mai",  // optionnel
  "send_email": true,      // si true, envoi email Brevo immédiat
  "send_sms": true         // si true, envoi SMS Brevo immédiat
}`}</pre>
        <p style={subTitle}>Exemple Node.js :</p>
        <pre style={codeBlock}>{`const r = await fetch('${baseUrl}/api/v1/partner/clients/208/payment-links', {
  method: 'POST',
  headers: {
    'X-API-Key': 'csk_xxx...',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    type: 'mensuel',
    amount: 89,
    send_email: true,
    send_sms: true
  })
});
const data = await r.json();
console.log(data.payment_link.url);  // URL Monetico complète
console.log(data.sent.email);        // { sent: true, shortUrl: "https://urlr.me/abc", messageId: "..." }
console.log(data.sent.sms);          // { sent: true, shortUrl: "https://urlr.me/abc", reference: "..." }`}</pre>
        <p style={subTitle}>Réponse :</p>
        <pre style={codeBlock}>{`{
  "payment_link": {
    "token": "abc123def456...",
    "url": "https://e-shops-catalogue.e-application.fr/p/abc123...",
    "amount": 89,
    "type": "mensuel",
    "created_at": "2026-05-20T10:30:00.000Z"
  },
  "sent": {
    "email": {
      "sent": true,
      "shortUrl": "https://urlr.me/abc123",
      "messageId": "<202605201030.xxxx@brevo>"
    },
    "sms": {
      "sent": true,
      "shortUrl": "https://urlr.me/abc123",
      "reference": "f7d6e5..."
    }
  }
}`}</pre>
        <p style={subTitle}>Si tu préfères envoyer toi-même (sans Brevo) :</p>
        <p style={{ color: '#475569', fontSize: '13px', margin: '4px 0 0' }}>
          Mets <code style={inlineCode}>send_email: false</code> et <code style={inlineCode}>send_sms: false</code>,
          tu récupères juste <code style={inlineCode}>payment_link.url</code> et tu l'utilises comme tu veux.
        </p>
      </div>

      {/* ═══════ 5. SUIVI PAIEMENTS ═══════ */}
      <h3 style={sectionTitle}>📊 4. Suivi des paiements Monetico</h3>

      <div style={endpointBox}>
        <div><span style={httpVerb('GET')}>GET</span><code style={inlineCode}>/payments</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>
          Liste des paiements Monetico des clients du commercial. Filtres :
          <code style={inlineCode}>statut</code> (PAYE, REFUSE, INCONNU),
          <code style={inlineCode}>since</code>, <code style={inlineCode}>until</code> (ISO-8601),
          <code style={inlineCode}>limit</code>, <code style={inlineCode}>offset</code>.
        </p>
        <pre style={codeBlock}>{`curl "${baseUrl}/api/v1/partner/payments?statut=PAYE&since=2026-05-01" \\
  -H "X-API-Key: csk_xxx..."`}</pre>
        <p style={subTitle}>Réponse :</p>
        <pre style={codeBlock}>{`{
  "total": 12,
  "limit": 50,
  "offset": 0,
  "payments": [
    {
      "reference": "AB123CD456",
      "statut": "PAYE",
      "montant": 89.00,
      "devise": "EUR",
      "mode": "mensuel",
      "tpe": "7285436",
      "monetico_date": "2026-05-20",
      "client_id": 208,
      "numero_client": "00000208",
      "client_nom": "Dupont",
      "client_prenom": "Jean",
      "cb_mask": "424242XXXXXX4242",
      "cb_type": "VISA",
      "auth_3ds": "Y",
      "created_at": "2026-05-20T11:42:33.000Z"
    }
  ]
}`}</pre>
      </div>

      <div style={endpointBox}>
        <div><span style={httpVerb('GET')}>GET</span><code style={inlineCode}>/payments/:reference</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>
          Détail d'un paiement par sa référence Monetico (unique).
        </p>
      </div>

      {/* ═══════ 5. OPÉRATIONS MONETICO ═══════ */}
      <h3 style={sectionTitle}>⚙️ 5. Opérations Monetico (pouvoir admin sur tes paiements)</h3>
      <p style={{ color: '#475569', lineHeight: 1.7, margin: '0 0 10px' }}>
        Mêmes pouvoirs que l'admin, <strong>limités aux paiements de tes clients</strong>. Les montants
        sont en euros. <code style={inlineCode}>:reference</code> = la référence Monetico du paiement.
      </p>

      <div style={endpointBox}>
        <div><span style={httpVerb('GET')}>GET</span><code style={inlineCode}>/payments/:reference/state</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>
          État réel du paiement chez Monetico (lecture seule). <code style={inlineCode}>etat</code> :
          <code style={inlineCode}>AU</code> autorisé en attente de recouvrement · <code style={inlineCode}>PA</code> recouvré (payé) ·
          <code style={inlineCode}>RE</code> refusé · <code style={inlineCode}>AN</code> annulé.
        </p>
        <pre style={codeBlock}>{`curl "${baseUrl}/api/v1/partner/payments/AB123CD456/state" \\
  -H "X-API-Key: csk_xxx..."
// → { "success": true, "etat": "PA", "montantrecouvre": "89EUR", "numauto": "102279" }`}</pre>
      </div>

      <div style={endpointBox}>
        <div><span style={httpVerb('POST')}>POST</span><code style={inlineCode}>/payments/:reference/capture</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>
          Recouvre (encaisse) le paiement. Total par défaut, ou <strong>partiel</strong> via
          <code style={inlineCode}>montant_a_capturer</code> (geste commercial : encaisser moins que le montant).
        </p>
        <pre style={codeBlock}>{`POST ${baseUrl}/api/v1/partner/payments/AB123CD456/capture
{ "montant_a_capturer": 50 }   // optionnel — défaut = montant total
// → { "success": true, "cdr": "1", "lib": "paiement accepte", "aut": "102279" }`}</pre>
      </div>

      <div style={endpointBox}>
        <div><span style={httpVerb('POST')}>POST</span><code style={inlineCode}>/payments/:reference/stop-recurrence</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>
          Résilie l'abonnement : stoppe les prélèvements récurrents futurs.
        </p>
        <pre style={codeBlock}>{`POST ${baseUrl}/api/v1/partner/payments/AB123CD456/stop-recurrence
// → { "success": true, "lib": "recurrence stoppee" }`}</pre>
      </div>

      <div style={endpointBox}>
        <div><span style={httpVerb('POST')}>POST</span><code style={inlineCode}>/payments/:reference/refund</code></div>
        <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '13px' }}>
          Rembourse le paiement. Total par défaut, ou <strong>partiel</strong> via <code style={inlineCode}>montant</code>.
        </p>
        <pre style={codeBlock}>{`POST ${baseUrl}/api/v1/partner/payments/AB123CD456/refund
{ "montant": 30 }   // optionnel — défaut = remboursement total
// → { "success": true, "cdr": "0", "lib": "recredit effectue" }`}</pre>
        <p style={{ margin: '6px 0 0', color: '#92400e', fontSize: '12px' }}>
          ⚠️ Le remboursement en production exige que l'IP de ton serveur soit autorisée chez Monetico.
        </p>
      </div>

      {/* ═══════ 6. WEBHOOK ═══════ */}
      <h3 style={sectionTitle}>🔔 6. Webhook (recommandé) — temps réel</h3>
      <p style={{ color: '#475569', lineHeight: 1.7 }}>
        Configure une <code style={inlineCode}>webhook_url</code> sur ta clé : nous POSTons sur cette URL
        à chaque événement de paiement. Plus besoin de poller <code style={inlineCode}>GET /payments</code>.
      </p>

      <p style={subTitle}>Événements :</p>
      <ul style={{ color: '#475569', lineHeight: 1.7, fontSize: '13px' }}>
        <li><code style={inlineCode}>payment.paid</code> — paiement réussi (statut <code>PAYE</code>)</li>
        <li><code style={inlineCode}>payment.refused</code> — paiement refusé</li>
        <li><code style={inlineCode}>payment.updated</code> — autre changement de statut</li>
      </ul>

      <p style={subTitle}>Headers envoyés :</p>
      <pre style={codeBlock}>{`POST <ta-url-webhook>
Content-Type: application/json
X-Webhook-Event: payment.paid
X-Webhook-Signature: 3a7f9b...  ← HMAC-SHA256(body, ton_secret_webhook)`}</pre>

      <p style={subTitle}>Body JSON :</p>
      <pre style={codeBlock}>{`{
  "event": "payment.paid",
  "delivered_at": "2026-05-20T11:42:35.123Z",
  "payment": {
    "reference": "AB123CD456",
    "statut": "PAYE",
    "montant": 89.00,
    "devise": "EUR",
    "mode": "mensuel",
    "tpe": "7285436",
    "monetico_date": "2026-05-20",
    "product_id": null,
    "product_name": null
  },
  "client": {
    "id": 208,
    "numero_client": "00000208",
    "nom": "Dupont",
    "prenom": "Jean",
    "email": "jean.dupont@example.com",
    "telephone": "0612345678",
    "entreprise": "Dupont SARL",
    "paye": true,
    "montant_paye": 89.00
  }
}`}</pre>

      <p style={subTitle}>⚠️ Vérification de signature (Node.js) :</p>
      <pre style={codeBlock}>{`const crypto = require('crypto');
const SECRET = 'TON_WEBHOOK_SECRET'; // donné lors de la création de la clé

app.post('/webhook/payments', express.raw({ type: 'application/json' }), (req, res) => {
  const signature = req.headers['x-webhook-signature'];
  const expected = crypto.createHmac('sha256', SECRET)
    .update(req.body) // req.body est le Buffer brut (express.raw)
    .digest('hex');

  if (signature !== expected) {
    return res.status(401).send('Invalid signature');
  }

  const payload = JSON.parse(req.body.toString());
  console.log(payload.event, payload.payment.reference, payload.payment.statut);
  // Mettre à jour ton CRM ici
  res.json({ ok: true });
});`}</pre>

      <p style={subTitle}>Vérification PHP :</p>
      <pre style={codeBlock}>{`<?php
$secret = 'TON_WEBHOOK_SECRET';
$body = file_get_contents('php://input');
$signature = $_SERVER['HTTP_X_WEBHOOK_SIGNATURE'] ?? '';
$expected = hash_hmac('sha256', $body, $secret);

if (!hash_equals($signature, $expected)) {
  http_response_code(401);
  exit('Invalid signature');
}

$payload = json_decode($body, true);
// Traitement...
http_response_code(200);
echo json_encode(['ok' => true]);`}</pre>

      <p style={subTitle}>Vérification Python (Flask) :</p>
      <pre style={codeBlock}>{`import hmac, hashlib
from flask import request, abort

SECRET = b'TON_WEBHOOK_SECRET'

@app.route('/webhook/payments', methods=['POST'])
def webhook():
    sig = request.headers.get('X-Webhook-Signature', '')
    expected = hmac.new(SECRET, request.data, hashlib.sha256).hexdigest()
    if not hmac.compare_digest(sig, expected):
        abort(401)
    payload = request.get_json()
    # Traitement...
    return {'ok': True}`}</pre>

      <p style={{ color: '#475569', fontSize: '13px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', padding: '10px 12px', marginTop: '10px' }}>
        💡 <strong>Bonne pratique</strong> : ton endpoint webhook doit répondre <strong>200 en moins de 5s</strong>.
        Pour des traitements lourds, mets le payload dans une queue et traite en arrière-plan.
        Si tu réponds non-2xx, l'événement est loggé comme échec mais <strong>nous ne retentons pas</strong> automatiquement.
      </p>

      {/* ═══════ 7. ERREURS ═══════ */}
      <h3 style={sectionTitle}>⚠️ 7. Codes d'erreur HTTP</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '8px' }}>
        <thead>
          <tr style={{ background: '#f1f5f9' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Code</th>
            <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Sens</th>
            <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Que faire</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700 }}>401</td>
            <td style={{ padding: '8px 12px' }}>Clé invalide / révoquée / header manquant</td>
            <td style={{ padding: '8px 12px', color: '#64748b' }}>Vérifier <code style={inlineCode}>X-API-Key</code></td>
          </tr>
          <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700 }}>403</td>
            <td style={{ padding: '8px 12px' }}>Ce client n'appartient pas au commercial de la clé</td>
            <td style={{ padding: '8px 12px', color: '#64748b' }}>Tu ne peux accéder qu'à TES clients</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700 }}>404</td>
            <td style={{ padding: '8px 12px' }}>Client / paiement introuvable</td>
            <td style={{ padding: '8px 12px', color: '#64748b' }}>Vérifier l'ID ou la référence</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700 }}>409</td>
            <td style={{ padding: '8px 12px' }}>Conflit (email/tel déjà existant lors d'une création)</td>
            <td style={{ padding: '8px 12px', color: '#64748b' }}>Le client existe déjà, utilise GET pour le retrouver</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700 }}>400</td>
            <td style={{ padding: '8px 12px' }}>Body invalide ou champ requis manquant</td>
            <td style={{ padding: '8px 12px', color: '#64748b' }}>Voir le champ <code style={inlineCode}>error</code> de la réponse</td>
          </tr>
          <tr>
            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700 }}>500</td>
            <td style={{ padding: '8px 12px' }}>Erreur serveur</td>
            <td style={{ padding: '8px 12px', color: '#64748b' }}>Nous contacter, réessayer plus tard</td>
          </tr>
        </tbody>
      </table>

      {/* ═══════ 8. CAS D'USAGE ═══════ */}
      <h3 style={sectionTitle}>🚀 8. Cas d'usage typiques</h3>

      <div style={{ ...endpointBox, background: '#f0fdf4', borderColor: '#86efac' }}>
        <strong style={{ color: '#15803d' }}>Cas 1 : Nouveau prospect signé dans ton CRM → on lui envoie le lien</strong>
        <ol style={{ marginTop: '8px', color: '#475569', fontSize: '13px', lineHeight: 1.7 }}>
          <li><code style={inlineCode}>POST /clients</code> pour créer la fiche (renvoie l'<code>id</code>)</li>
          <li><code style={inlineCode}>POST /clients/&lt;id&gt;/payment-links</code> avec <code>send_email: true, send_sms: true</code></li>
          <li>Le client reçoit email + SMS contenant le lien <code>https://urlr.me/xxx</code></li>
          <li>Webhook <code>payment.paid</code> arrive sur ton serveur quand il a payé</li>
        </ol>
      </div>

      <div style={{ ...endpointBox, background: '#f0f9ff', borderColor: '#7dd3fc' }}>
        <strong style={{ color: '#0369a1' }}>Cas 2 : Tableau de bord paiements dans ton CRM</strong>
        <ol style={{ marginTop: '8px', color: '#475569', fontSize: '13px', lineHeight: 1.7 }}>
          <li>Au chargement de ton dashboard : <code style={inlineCode}>GET /payments?statut=PAYE&since=2026-01-01</code></li>
          <li>Affiche la liste avec <code>client_nom</code>, <code>montant</code>, <code>monetico_date</code></li>
          <li>Configure le webhook pour rafraîchir en live sans recharger</li>
        </ol>
      </div>

      <div style={{ ...endpointBox, background: '#fef3c7', borderColor: '#fcd34d' }}>
        <strong style={{ color: '#92400e' }}>Cas 3 : Relancer un client qui n'a pas payé</strong>
        <ol style={{ marginTop: '8px', color: '#475569', fontSize: '13px', lineHeight: 1.7 }}>
          <li><code style={inlineCode}>GET /clients/&lt;id&gt;</code> → vérifier <code>paye: false</code></li>
          <li><code style={inlineCode}>POST /clients/&lt;id&gt;/payment-links</code> avec un nouveau montant ou même montant</li>
          <li>Le client reçoit un nouveau lien (l'ancien reste valable s'il n'a pas expiré)</li>
        </ol>
      </div>

      {/* ═══════ 9. LIMITES & SÉCURITÉ ═══════ */}
      <h3 style={sectionTitle}>🛡️ 9. Sécurité & bonnes pratiques</h3>
      <ul style={{ color: '#475569', lineHeight: 1.8, fontSize: '13px' }}>
        <li>🔐 <strong>Stocker la clé en variable d'environnement</strong> côté serveur, jamais en clair dans le code frontend</li>
        <li>🔄 <strong>Régénérer la clé</strong> immédiatement en cas de fuite (révoque l'ancienne)</li>
        <li>📝 <strong>Logger les requêtes</strong> côté ton CRM pour audit</li>
        <li>⏱️ <strong>Rate limit recommandé</strong> : 10 req/s, pas de bursts massifs</li>
        <li>🔍 <strong>Toutes les actions sont tracées</strong> de notre côté (dernière utilisation + IP visible dans cette page)</li>
        <li>✉️ <strong>Email/SMS via Brevo</strong> : les liens sont raccourcis (urlr.me) pour éviter le marquage spam</li>
      </ul>

      <div style={{ background: '#0f172a', color: 'white', borderRadius: '10px', padding: '18px', marginTop: '20px', textAlign: 'center' }}>
        <strong style={{ fontSize: '14px' }}>Besoin d'aide pour l'intégration ?</strong>
        <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#cbd5e1' }}>
          Écrivez-nous, nous vous accompagnons pour brancher votre boutique.
        </p>
      </div>
    </div>
  );
}

export default PartnerApiKeys;
